import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import LinkIcon from "@mui/icons-material/Link";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import { useStore } from "../../store";
import {
  HLogo,
  InddedLogo,
  Linkdeinlogo,
  Naukrilogo,
  Shinelogo,
  founditlogo,
  herkeylogo,
  internshalalogo,
  simplyhiredlogo,
  talentlogo,
} from "../assets";

// Same empty shape handleSubmit already falls back to per-field when an upload's
// extraction partially fails — reused here so "start from scratch" renders through
// the exact same TemplateSelection/ResumePreview path a real upload does, just with
// every field blank instead of extracted.
const BLANK_RESUME_DATA = {
  basics: {
    name: "",
    email: "",
    phone: "",
    headline: "",
    location: "",
    url: { href: "", label: "" },
    picture: { url: "" },
    customFields: [],
  },
  sections: {
    skills: {
      name: "Skills",
      items: [{ name: "", level: 3, visible: true }],
    },
    summary: { name: "Summary", content: "", visible: true },
    profiles: {
      name: "Profiles",
      items: [{ name: "", url: { href: "", label: "" } }],
    },
    projects: {
      name: "Projects",
      items: [{ name: "", description: "", skills: [""] }],
    },
    interests: { name: "Interests", items: [{ name: "" }] },
    languages: {
      name: "Languages",
      items: [{ name: "", level: 0, proficiency: "100%" }],
    },
    volunteer: {
      name: "Volunteering",
      items: [
        {
          position: "",
          company: "",
          location: "",
          date: "",
          summary: "",
          visible: true,
        },
      ],
    },
    experience: {
      name: "Experience",
      items: [
        {
          position: "",
          company: "",
          location: "",
          date: "",
          summary: "",
          missions: [""],
          visible: true,
        },
      ],
    },
    references: {
      name: "References",
      items: [],
      columns: 1,
      visible: true,
      separateLinks: true,
    },
    publications: {
      name: "Publications",
      items: [
        {
          name: "",
          description: "",
          issuer: "",
          date: "",
          url: { href: "", label: "" },
        },
      ],
    },
    certifications: {
      name: "Certifications",
      items: [
        {
          name: "",
          description: "",
          issuer: "",
          date: "",
          url: { href: "", label: "" },
        },
      ],
    },
    awards: {
      name: "Awards",
      items: [{ name: "", description: "", issuer: "", date: "" }],
    },
    education: {
      name: "Education",
      items: [
        {
          institution: "",
          studyType: "",
          area: "",
          date: "",
          summary: "",
          score: "",
          url: { href: "", label: "" },
        },
      ],
    },
    current_skills: {
      name: "Current Skills",
      items: [{ name: "", level: 3, visible: true }],
    },
    missing_skills: {
      name: "Missing Skills",
      items: [{ name: "", level: 3, visible: true }],
    },
    suggested_missions: {
      name: "Suggested Missions",
      items: [""],
    },
  },
};

const FileUploadForm = () => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  const { setData, profilePicture, setProfilePicture, loading, setLoading } =
    useStore();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLinkClear = () => {
    setLinkUrl("");
  };

  const handleDescriptionClear = () => {
    setText("");
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleStartFromScratch = () => {
    setData(BLANK_RESUME_DATA);
    localStorage.setItem("resume_data", JSON.stringify(BLANK_RESUME_DATA));
    setMessage("Starting a new resume from scratch — pick a template below and fill it in.");
  };

  const handleProfilePictureChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePicture(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setMessage("");

    try {
      try {
        const responseTextExtraction = await axios.post(
          "https://api.interviewaxis.com/modifier/text_extraction",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const endpoints = [
          "basics",
          "summary",
          "profiles",
          "projects",
          "interests",
          "languages",
          "volunteer",
          "experience",
          "references",
          "publications",
          "certifications",
          "awards",
          "education",
          "skills",
        ];

        const data = {
          basics: {
            name: "",
            email: "",
            phone: "",
            headline: "",
            location: "",
            url: { href: "", label: "" },
            picture: { url: "" },
            customFields: [],
          },
          sections: {
            skills: {
              name: "Skills",
              items: [{ name: "", level: 3, visible: true }],
            },
            summary: { name: "Summary", content: "", visible: true },
            profiles: {
              name: "Profiles",
              items: [{ name: "", url: { href: "", label: "" } }],
            },
            projects: {
              name: "Projects",
              items: [{ name: "", description: "", skills: [""] }],
            },
            interests: { name: "Interests", items: [{ name: "" }] },
            languages: {
              name: "Languages",
              items: [{ name: "", level: 0, proficiency: "100%" }],
            },
            volunteer: {
              name: "Volunteering",
              items: [
                {
                  position: "",
                  company: "",
                  location: "",
                  date: "",
                  summary: "",
                  visible: true,
                },
              ],
            },
            experience: {
              name: "Experience",
              items: [
                {
                  position: "",
                  company: "",
                  location: "",
                  date: "",
                  summary: "",
                  missions: [""],
                  visible: true,
                },
              ],
            },
            references: {
              name: "References",
              items: [],
              columns: 1,
              visible: true,
              separateLinks: true,
            },
            publications: {
              name: "Publications",
              items: [
                {
                  name: "",
                  description: "",
                  issuer: "",
                  date: "",
                  url: { href: "", label: "" },
                },
              ],
            },
            certifications: {
              name: "Certifications",
              items: [
                {
                  name: "",
                  description: "",
                  issuer: "",
                  date: "",
                  url: { href: "", label: "" },
                },
              ],
            },
            awards: {
              name: "Awards",
              items: [{ name: "", description: "", issuer: "", date: "" }],
            },
            education: {
              name: "Education",
              items: [
                {
                  institution: "",
                  studyType: "",
                  area: "",
                  date: "",
                  summary: "",
                  score: "",
                  url: { href: "", label: "" },
                },
              ],
            },
            current_skills: {
              name: "Current Skills",
              items: [{ name: "", level: 3, visible: true }],
            },
            missing_skills: {
              name: "Missing Skills",
              items: [{ name: "", level: 3, visible: true }],
            },
            suggested_missions: {
              name: "Suggested Missions",
              items: [""],
            },
          },
        };

        for (const endpoint of endpoints) {
          try {
            const response = await axios.post(
              `https://api.interviewaxis.com/modifier/${endpoint}`,
              {
                text_extraction: responseTextExtraction.data,
              },
              {
                headers: { "Content-Type": "application/json" },
              }
            );
            if (endpoint === "basics") {
              data["basics"] = response.data;
            } else {
              data["sections"][endpoint] = response.data;
            }

            setData(data);
          } catch (error) {
            console.error(`Error in ${endpoint}:`, error);
            if (endpoint === "basics") {
              data["basics"] = null;
            } else {
              data["sections"][endpoint] = null;
            }
          }
        }

        const responseUpdateData = await axios.post(
          "https://api.interviewaxis.com/modifier/update_data",
          {
            job_description: text,
            resume_json: data,
            extracted_text: responseTextExtraction.data,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        setData(responseUpdateData.data);
        localStorage.setItem(
          "extracted_text",
          JSON.stringify(responseTextExtraction.data)
        );
        localStorage.setItem("job_description", JSON.stringify(text));
        localStorage.setItem(
          "resume_data",
          JSON.stringify(responseUpdateData.data)
        );
        setMessage("File uploaded successfully!");
      } catch (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          console.error(
            "Server responded with an error:",
            error.response.status,
            error.response.data
          );
        } else if (error.request) {
          // The request was made but no response was received
          console.error("No response received:", error.request);
        } else {
          // Something else went wrong
          console.error("Error:", error.message);
        }
      }
    } catch (error) {
      setMessage("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromLink = async () => {
    if (!linkUrl) return;

    setLinkLoading(true);

    try {
      const response = await axios.post(
        "https://jdscraper.interviewaxis.com/extract",
        {
          url: linkUrl,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log(JSON.stringify(response.data.data));

      // Set the text with the received data
      if (response.data && response.data.data) {
        // Check if the data is an object and extract just the job description
        if (typeof response.data.data === "object") {
          // If it's a job description object with job_description field
          if (response.data.data.job_description) {
            // Replace literal \n with actual line breaks
            const formattedText = response.data.data.job_description.replace(
              /\\n/g,
              "\n"
            );
            setText(formattedText);
          } else {
            // For other objects, extract just the content without the JSON structure
            const extractedText = Object.values(response.data.data).join(
              "\n\n"
            );
            setText(extractedText.replace(/\\n/g, "\n"));
          }
        } else if (typeof response.data.data === "string") {
          // Try to parse if it's a JSON string
          try {
            const parsedData = JSON.parse(response.data.data);
            if (parsedData.job_description) {
              const formattedText = parsedData.job_description.replace(
                /\\n/g,
                "\n"
              );
              setText(formattedText);
            } else {
              setText(response.data.data.replace(/\\n/g, "\n"));
            }
          } catch (e) {
            // Not valid JSON, just use as is
            setText(response.data.data.replace(/\\n/g, "\n"));
          }
        } else {
          setText(String(response.data.data).replace(/\\n/g, "\n"));
        }

        // Switch to the manual tab
        setActiveTab(1);
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error(
          "Server responded with an error:",
          error.response.status,
          error.response.data
        );
        setMessage("Failed to fetch data from URL. Server error.");
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received:", error.request);
        setMessage("Failed to fetch data from URL. No response received.");
      } else {
        // Something else went wrong
        console.error("Error:", error.message);
        setMessage("Failed to fetch data from URL. " + error.message);
      }
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: { xs: "none", md: 700 },
        mx: "auto",
        textAlign: "center",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Profile Picture Upload Field */}
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "48px 0 0 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "2px dashed #ccc",
                borderRadius: "50%",
                width: "160px",
                mb: 2,
                aspectRatio: 1,
                backgroundColor: "#f9f9f9",
                position: "relative",
              }}
            >
              <img
                src={
                  profilePicture ? profilePicture : "assets/placeholder_pp.jpg"
                }
                alt="Profile Preview"
                style={{
                  width: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                  aspectRatio: 1,
                }}
              />
              <Button
                variant="contained"
                component="label"
                sx={{
                  position: "absolute",
                  bottom: "5px",
                  right: "5px",
                  backgroundColor: "#0D1E30",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "50%",
                  padding: "8px",
                  minWidth: "35px",
                  height: "35px",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#0A1723",
                  },
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                />
                ✎
              </Button>
            </Box>
          </Grid>

          {/* Job Description Input - Enhanced UI */}
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              padding: "48px 0 0 0",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
                position: "absolute",
                transform: "translate(-360px, 0)",
              }}
            >
              <Box
                sx={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  border: "2px solid #1976d2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  color: "#1976d2",
                  fontWeight: "bold",
                  fontSize: "16px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                01
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "medium",
                  color: "#555",
                  textAlign: "center",
                }}
              >
                Step
              </Typography>
            </Box>

            {/* Enhanced Job Description Paper */}
            <Paper
              elevation={3}
              sx={{
                padding: "24px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                textAlign: "center",
                width: { xs: "100%", md: "30vw" },
                margin: "0 auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", marginBottom: "16px", color: "#333" }}
              >
                Type or paste a job description
              </Typography>

              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ mb: 2 }}
                indicatorColor="primary"
              >
                <Tab
                  icon={<LinkIcon />}
                  label="URL"
                  iconPosition="start"
                  sx={{ fontWeight: 500, textTransform: "none" }}
                />
                <Tab
                  icon={<DescriptionIcon />}
                  label="Manual"
                  iconPosition="start"
                  sx={{ fontWeight: 500, textTransform: "none" }}
                />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Paste the job posting URL"
                    variant="outlined"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com/job-posting"
                    disabled={linkLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: linkUrl && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleLinkClear}
                            edge="end"
                            size="small"
                            disabled={linkLoading}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFetchFromLink}
                    disabled={!linkUrl || linkLoading}
                    fullWidth
                    sx={{
                      backgroundColor: "#002855",
                      color: "#fff",
                      textTransform: "none",
                      padding: "10px",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#004080",
                      },
                    }}
                  >
                    {linkLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CircularProgress
                          size={24}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        Loading...
                      </Box>
                    ) : (
                      "Fetch Job Description"
                    )}
                  </Button>
                </Box>
              )}

              {activeTab === 1 && (
                <TextField
                  fullWidth
                  label="Type or paste job description"
                  variant="outlined"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  multiline
                  rows={6}
                  placeholder="Enter the full job description here..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ alignSelf: "flex-start", mt: 1.5 }}
                      >
                        <ContentPasteIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: text && (
                      <InputAdornment
                        position="end"
                        sx={{ alignSelf: "flex-start", mt: 1.5 }}
                      >
                        <IconButton
                          onClick={handleDescriptionClear}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Typography
                variant="body2"
                sx={{
                  marginTop: "8px",
                  color: "#666",
                  fontSize: "0.8rem",
                  textAlign: "left",
                }}
              >
                Tip: Include key requirements, responsibilities, and
                qualifications for better results
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  marginTop: 3,
                }}
              >
                <img
                  src={Naukrilogo}
                  alt="Naukri"
                  style={{
                    height: "24px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={HLogo}
                  alt="HLogo"
                  style={{
                    height: "24px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={Linkdeinlogo}
                  alt="LinkedIn"
                  style={{
                    height: "22px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={InddedLogo}
                  alt="Indeed"
                  style={{
                    height: "22px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={Shinelogo}
                  alt="Shine"
                  style={{
                    height: "24px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={founditlogo}
                  alt="founfit"
                  style={{
                    height: "14px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={talentlogo}
                  alt="talent"
                  style={{
                    height: "15px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                <img
                  src={internshalalogo}
                  alt="internshala"
                  style={{
                    height: "20px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* File Upload Field */}
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              padding: "48px 0 0 0",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
                position: "absolute",
                transform: "translate(-360px, 0)",
              }}
            >
              <Box
                sx={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  border: "2px solid #1976d2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  color: "#1976d2",
                  fontWeight: "bold",
                  fontSize: "16px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                02
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "medium",
                  color: "#555",
                  textAlign: "center",
                }}
              >
                Step
              </Typography>
            </Box>

            <Paper
              elevation={3}
              sx={{
                padding: "24px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                textAlign: "center",
                width: { xs: "100%", md: "30vw" },
                margin: "0 auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", marginBottom: "16px", color: "#333" }}
              >
                Upload your Resume
              </Typography>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{
                  backgroundColor: "#002855",
                  color: "#fff",
                  textTransform: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#004080",
                  },
                }}
              >
                Upload File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected File: {file.name}
                </Typography>
              )}

              <Typography
                variant="body2"
                sx={{
                  marginTop: "16px",
                  color: "#666",
                }}
              >
                DOC, DOCX, PDF (Max - 10MB)
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  marginTop: "16px",
                  color: "#666",
                }}
              >
                Don't have a resume?
              </Typography>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={handleStartFromScratch}
                sx={{
                  marginTop: "8px",
                  borderColor: "#002855",
                  color: "#002855",
                  textTransform: "none",
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "#004080",
                    backgroundColor: "rgba(0, 40, 85, 0.04)",
                  },
                }}
              >
                Build One From Scratch
              </Button>
            </Paper>
          </Grid>

          {/* Submit Button */}
          <Grid
            item
            xs={12}
            style={{
              padding: "48px 0 0 0",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{
                width: { xs: "100%", md: "30vw" },
                backgroundColor: "red",
                color: "white",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                padding: "10px 20px",
                "&:hover": {
                  backgroundColor: "darkred",
                },
                "&:disabled": {
                  backgroundColor: "lightgray",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Uploading...
                </Box>
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>

      {/* Message */}
      {message && (
        <Typography
          variant="body2"
          color={message.includes("successfully") ? "green" : "error"}
          sx={{ mt: 2 }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploadForm;
